import type { Status } from '@cencosud-cencommerce/paris-uikit/dist/atoms/Inputs/TextField/partials/TextFieldBase';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { Cart } from '@/modules/cart/domain/Cart';
import type { TPaymentMethodId } from '@/modules/perseo-billing/components/accordion/interfaces';
import type { ECencopayModalOriginCancelType } from '@/modules/perseo-billing/containers/payment-methods/components/cencopay';
import type { IAvailablePaymentMethodsResponse } from '@/modules/perseo-billing/interfaces';
import type {
  CencoFee,
  CreditFee,
  EPayStatus,
  IBillingPaymentMethods,
  IOrderConfirmation,
  IOrderIntentResponse
} from '@/modules/perseo-billing/interfaces/src/bff-web/billing';
import type {
  IPaymentRequest,
  ISinglePayment
} from '@/modules/perseo-billing/interfaces/src/bff-web/billing/interfaces/payment-methods.interfaceV2';
import type { IProviderProductItem } from '@/modules/perseo-billing/interfaces/src/shared/interfaces/product-group';
import type {
  IErrorHandlerBilling,
  IErrorResponse
} from '@/modules/perseo-billing/utils/error-handler';

export type TCompany = {
  id: string;
  officialName: string;
  areaOfWork: string;
  address: string;
  numberOfHouseApartmentOrOffice: string;
  regionName: string;
  regionId: string;
  communeId: string;
  fullAddress: string;
};

// index from the accordion and a index from the item inside the accordion
export type TactiveAccordion = {
  activeAccordionIndex: number | undefined;
  activeItemIndex: number | undefined;
  accordionPaymentMethodId?: TPaymentMethodId;
} | null;

export interface GenericToggle {
  title: string;
  value: boolean;
}

type TCouponData = {
  helperText?: string;
  buttonDisabled: boolean;
  status?: Status;
  value?: string;
} | null;
export const billingSlice = createSlice({
  name: 'billingSlice',
  initialState: {
    availablePayments: null as IAvailablePaymentMethodsResponse | null,
    activeCart: null as Cart | null,
    billingSummary: null as IBillingPaymentMethods | null,
    paymentSuccess: null as IOrderConfirmation | null,
    isPaymentComplete: false as boolean,
    isProcessingPayment: false as boolean,
    transactionID: null as unknown as string,
    isSummaryExpanded: false as boolean,
    activeAccordion: null as TactiveAccordion | null,
    disableGiftCard: false as boolean,
    disableCencopay: false as boolean,
    kioscoOrderIntent: null as IOrderIntentResponse | null,
    kioscoOrderIntentError: null as IErrorResponse | null,
    kioscoStatus: null as EPayStatus | null,
    selectedPaymentMethods: null as IPaymentRequest | null,
    paymentError: null as IErrorHandlerBilling | null,
    giftCardError: null as IErrorHandlerBilling | null,
    leavingPayment: '',
    isSignUpModalOpen: false as boolean,
    isClubParisModalOpen: false as boolean | null,
    isRemoveClubParisModalOpen: false as boolean,
    isGiftCardModalFindItOpen: false as boolean,
    isGiftCardModalRemoveOpen: false as boolean,
    isCouponModalRemoveOpen: false as boolean,
    isNeedInvoiceModalOpen: false as boolean,
    isDeleteCompanyModalOpen: false as boolean,
    companies: [] as TCompany[],
    isInvoiceAndGiftCardAndClubParisModalOpen: false as boolean,
    isModalQRCencopayVisible: false as boolean,
    cancelOrderChangePaymentMethod:
      null as ECencopayModalOriginCancelType | null,
    isInvoiceAndGuestModalOpen: false as boolean,
    isGiftCartInsufficientModalOpen: false as boolean,
    isGiftCartIncompatibleProductsModalOpen: false as boolean,
    isGiftCartIncompatibleProductsAllModalOpen: false as boolean,
    isPaymentAvailable: false as boolean,
    productsNotCompatibleWithGC: [] as IProviderProductItem[],
    isKeyboardOpen: false as boolean,
    cencoPayQR: null as IOrderIntentResponse | null,
    cencoPayStatus: null as EPayStatus | null | undefined,
    isModalCencopayGetTheAppVisible: false as boolean,
    isCencoPayTimeOut: false as boolean,
    isCatEnabled: false as boolean,
    cencosudFees: [] as CencoFee[],
    creditFees: [] as CreditFee[],
    couponData: null as TCouponData
  },
  reducers: {
    setBillingSummary: (
      state,
      { payload: data }: PayloadAction<IBillingPaymentMethods>
    ) => {
      state.billingSummary = {
        ...state.billingSummary,
        ...data
      };
    },
    setKioscoOrderIntent: (
      state,
      { payload: data }: PayloadAction<IOrderIntentResponse>
    ) => {
      state.kioscoOrderIntent = data;
    },
    setKioscoOrderIntentError: (
      state,
      { payload: data }: PayloadAction<IErrorResponse | null>
    ) => {
      state.kioscoOrderIntentError = data;
    },
    setKioscoStatus: (state, { payload: data }: PayloadAction<EPayStatus>) => {
      state.kioscoStatus = data;
    },
    setPaymentSuccess: (
      state,
      { payload: data }: PayloadAction<IOrderConfirmation>
    ) => {
      state.paymentSuccess = {
        ...state.paymentSuccess,
        ...data,
        id: data?.id ?? state.paymentSuccess?.id ?? ''
      };
    },
    setIsProcessingPayment: (state, action: PayloadAction<boolean>) => {
      state.isProcessingPayment = action.payload;
    },
    setTransactionID: (state, action: PayloadAction<string>) => {
      state.transactionID = action.payload;
    },

    setIsPaymentComplete: (state, action: PayloadAction<boolean>) => {
      state.isPaymentComplete = action.payload;
    },
    setIsSummaryExpanded: (state, action: PayloadAction<boolean>) => {
      state.isSummaryExpanded = action.payload;
    },
    setActiveAccordion: (state, action: PayloadAction<TactiveAccordion>) => {
      state.activeAccordion = action.payload;
      if (!action.payload) {
        state.isPaymentAvailable = false;
      }
    },
    setSelectedPaymentMethods: (
      state,
      { payload: data }: PayloadAction<IPaymentRequest | undefined>
    ) => {
      if (data) {
        if (data.payments && data.payments.length > 2) {
          data.payments = data.payments.slice(0, 2);
        }
        state.selectedPaymentMethods = {
          ...state.selectedPaymentMethods,
          ...data
        };
      } else {
        state.selectedPaymentMethods = null;
      }
    },
    addToSelectedPaymentMethods: (
      state,
      { payload: data }: PayloadAction<IPaymentRequest>
    ) => {
      if (!data.payments) return;

      if (state.selectedPaymentMethods?.payments) {
        state.selectedPaymentMethods.payments.push(...data.payments);
      } else {
        state.selectedPaymentMethods = data;
      }
    },
    addPaymentMethod: (
      state,
      { payload: paymentMethod }: PayloadAction<ISinglePayment>
    ) => {
      if (state.selectedPaymentMethods) {
        state.selectedPaymentMethods.payments = [
          ...(state.selectedPaymentMethods.payments || []),
          paymentMethod
        ];
      } else {
        state.selectedPaymentMethods = {
          payments: [paymentMethod]
        };
      }
    },
    setDisableGiftCard: (state, action: PayloadAction<boolean>) => {
      state.disableGiftCard = action.payload;
    },
    setPaymentError: (
      state,
      action: PayloadAction<IErrorHandlerBilling | null>
    ) => {
      state.paymentError = action.payload;
    },
    setGiftCardError: (
      state,
      action: PayloadAction<IErrorHandlerBilling | null>
    ) => {
      state.giftCardError = action.payload;
    },
    setLeavingPayment: (state, action: PayloadAction<string>) => {
      state.leavingPayment = action.payload;
    },
    setPaymentAvailable: (state, action: PayloadAction<boolean>) => {
      state.isPaymentAvailable = action.payload;
    },
    setIsSignUpModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isSignUpModalOpen = action.payload;
    },
    setIsClubParisModalOpen: (state, action: PayloadAction<boolean | null>) => {
      state.isClubParisModalOpen = action.payload;
    },
    setIsRemoveClubParisModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isRemoveClubParisModalOpen = action.payload;
    },
    setIsGiftCardModalFindItOpen: (state, action: PayloadAction<boolean>) => {
      state.isGiftCardModalFindItOpen = action.payload;
    },
    setIsGiftCardModalRemoveOpen: (state, action: PayloadAction<boolean>) => {
      state.isGiftCardModalRemoveOpen = action.payload;
    },
    setIsCouponModalRemoveOpen: (state, action: PayloadAction<boolean>) => {
      state.isCouponModalRemoveOpen = action.payload;
    },
    setIsNeedInvoiceModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isNeedInvoiceModalOpen = action.payload;
    },
    setIsDeleteCompanyModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isDeleteCompanyModalOpen = action.payload;
    },
    setIsInvoiceAndGiftCardAndClubParisModalOpen: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.isInvoiceAndGiftCardAndClubParisModalOpen = action.payload;
    },
    setIsModalQRCencopayVisible: (state, action: PayloadAction<boolean>) => {
      state.isModalQRCencopayVisible = action.payload;
    },
    setIsGenericModalVisible: (state, action: PayloadAction<GenericToggle>) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      state[action.payload.title] = action.payload.value;
    },
    setCancelOrderChangePaymentMethod: (
      state,
      action: PayloadAction<ECencopayModalOriginCancelType | null>
    ) => {
      state.cancelOrderChangePaymentMethod = action.payload;
    },
    setIsInvoiceAndGuestModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isInvoiceAndGuestModalOpen = action.payload;
    },
    setIsGiftCardInsufficientModalOpen: (
      state,
      action: PayloadAction<boolean | undefined>
    ) => {
      state.isGiftCartInsufficientModalOpen =
        action.payload ?? !state.isGiftCartInsufficientModalOpen;
    },
    setIsGiftCardIncompatibleProductsModalOpen: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.isGiftCartIncompatibleProductsModalOpen = action.payload;
    },
    setIsGiftCardIncompatibleProductsAllModalOpen: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.isGiftCartIncompatibleProductsAllModalOpen = action.payload;
    },
    deleteCompany: (state, action: PayloadAction<{ companyId: string }>) => {
      state.companies = state.companies.filter(
        (company) => company.id !== action.payload.companyId
      );
    },
    // * Invoice form can add or edit company. This solves both cases.
    putCompany: (state, action: PayloadAction<{ companyData: TCompany }>) => {
      const { companyData } = action.payload;
      state.companies = [companyData];
    },
    setProductsNotCompatibleWithGC: (
      state,
      action: PayloadAction<IProviderProductItem[]>
    ) => {
      state.productsNotCompatibleWithGC = action.payload;
    },
    setIsKeyboardOpen: (state, action: PayloadAction<boolean>) => {
      state.isKeyboardOpen = action.payload;
    },
    setQRData: (state, action: PayloadAction<IOrderIntentResponse | null>) => {
      state.cencoPayQR = action.payload;
    },
    setCencoPayStatus: (state, action: PayloadAction<EPayStatus>) => {
      state.cencoPayStatus = action.payload;
    },
    setIsModalCencopayGetTheAppVisible: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.isModalCencopayGetTheAppVisible = action.payload;
    },
    setIsCencoPayTimeOut: (state, action: PayloadAction<boolean>) => {
      state.isCencoPayTimeOut = action.payload;
    },
    setCatMethodEnabled: (state) => {
      state.isCatEnabled = true;
    },
    setAvailablePayments: (
      state,
      { payload }: PayloadAction<IAvailablePaymentMethodsResponse>
    ) => {
      state.availablePayments = payload;
      state.creditFees = payload.creditFees ?? [];
      state.cencosudFees = payload.cencoFees ?? [];
    },
    setActiveCart: (state, { payload }: PayloadAction<Cart>) => {
      state.activeCart = payload;
    },
    setCouponData: (state, { payload }: PayloadAction<TCouponData>) => {
      state.couponData = payload;
    }
  }
});

export const billingReducer = billingSlice.reducer;
